import React, { useState, useEffect } from 'react';
import axios from '../../axiosConfig';
import { FiPlus, FiTrash2, FiChevronUp, FiChevronDown, FiSave, FiHelpCircle, FiFileText } from 'react-icons/fi';
import DeleteConfirmationModal from './DeleteConfirmationModal';
import ReportTemplatePreviewModal from './ReportTemplatePreviewModal';
import ReportAssignmentModal from './ReportAssignmentModal';
import '../../styles/TemplatesManagement.css';

const TemplatesManagement = ({ templates: initialTemplates, schoolId, onTemplatesChange }) => {
    const [templates, setTemplates] = useState(initialTemplates || []);
    const [savedQuestions, setSavedQuestions] = useState([]);

    // Modal states
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
    const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);

    // Selected items
    const [templateToDelete, setTemplateToDelete] = useState(null);
    const [templateToPreview, setTemplateToPreview] = useState(null);
    const [templateToAssign, setTemplateToAssign] = useState(null);

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    // Template form state
    const [templateForm, setTemplateForm] = useState({
        title: '',
        questions: []
    });

    // Question addition mode
    const [questionMode, setQuestionMode] = useState('new-save');
    const [selectedSavedQuestion, setSelectedSavedQuestion] = useState('');
    const [newQuestionText, setNewQuestionText] = useState('');

    // Sync templates with parent
    useEffect(() => {
        setTemplates(initialTemplates || []);
    }, [initialTemplates]);

    // Notify parent of changes
    const updateTemplates = (newTemplates) => {
        setTemplates(newTemplates);
        if (onTemplatesChange) {
            onTemplatesChange(newTemplates);
        }
    };

    // Fetch saved questions
    const fetchSavedQuestions = async () => {
        try {
            const token = localStorage.getItem('jwt');
            const response = await axios.get(`/api/v1/reports/questions/school/${schoolId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setSavedQuestions(response.data);
        } catch (err) {
            console.error('Error fetching saved questions:', err);
        }
    };

    useEffect(() => {
        fetchSavedQuestions();
    }, [schoolId]);

    // Reset form
    const resetTemplateForm = () => {
        setTemplateForm({
            title: '',
            questions: []
        });
        setQuestionMode('new-save');
        setSelectedSavedQuestion('');
        setNewQuestionText('');
    };

    // Handle form changes
    const handleTitleChange = (e) => {
        setTemplateForm({ ...templateForm, title: e.target.value });
    };

    const addQuestionToTemplate = () => {
        let questionToAdd = {
            questionText: '',
            orderIndex: templateForm.questions.length,
            saveToDatabase: false
        };

        if (questionMode === 'saved') {
            const question = savedQuestions.find(q => q.id === selectedSavedQuestion);
            if (!question) {
                setError('Proszę wybrać zapisane pytanie');
                return;
            }
            questionToAdd.questionText = question.question;
            questionToAdd.questionId = question.id;
            questionToAdd.isExistingQuestion = true;
        } else if (questionMode === 'new-save' || questionMode === 'new-temp') {
            if (!newQuestionText.trim()) {
                setError('Treść pytania nie może być pusta');
                return;
            }
            questionToAdd.questionText = newQuestionText;
            questionToAdd.saveToDatabase = questionMode === 'new-save';
        }

        setTemplateForm({
            ...templateForm,
            questions: [...templateForm.questions, questionToAdd]
        });

        setSelectedSavedQuestion('');
        setNewQuestionText('');
    };

    const removeQuestion = (index) => {
        const updatedQuestions = templateForm.questions.filter((_, i) => i !== index);
        updatedQuestions.forEach((q, i) => q.orderIndex = i);
        setTemplateForm({ ...templateForm, questions: updatedQuestions });
    };

    const moveQuestion = (index, direction) => {
        const newIndex = direction === 'up' ? index - 1 : index + 1;
        if (newIndex < 0 || newIndex >= templateForm.questions.length) return;

        const updatedQuestions = [...templateForm.questions];
        const temp = updatedQuestions[index];
        updatedQuestions[index] = updatedQuestions[newIndex];
        updatedQuestions[newIndex] = temp;

        updatedQuestions.forEach((q, i) => q.orderIndex = i);
        setTemplateForm({ ...templateForm, questions: updatedQuestions });
    };

    // Create template
    const handleCreateTemplate = async () => {
        if (!templateForm.title.trim()) {
            setError('Tytuł szablonu nie może być pusty');
            return;
        }

        if (templateForm.questions.length === 0) {
            setError('Szablon musi zawierać przynajmniej jedno pytanie');
            return;
        }

        try {
            setLoading(true);
            const token = localStorage.getItem('jwt');

            // Save new questions to database if needed
            const questionsToSave = templateForm.questions
                .filter(q => q.saveToDatabase && !q.isExistingQuestion)
                .map(q => ({ question: q.questionText, schoolId }));

            let savedQuestionsMap = {};

            if (questionsToSave.length > 0) {
                const savedQuestionsPromises = questionsToSave.map(q =>
                    axios.post('/api/v1/reports/questions', q, {
                        headers: { Authorization: `Bearer ${token}` }
                    })
                );

                const savedQuestionsResponses = await Promise.all(savedQuestionsPromises);

                savedQuestionsMap = savedQuestionsResponses.reduce((map, response, index) => {
                    map[questionsToSave[index].question] = response.data.id;
                    return map;
                }, {});

                fetchSavedQuestions();
            }

            // Prepare template questions
            const templateQuestions = templateForm.questions.map(q => {
                if (q.isExistingQuestion && q.questionId) {
                    return {
                        questionId: q.questionId,
                        questionText: q.questionText,
                        orderIndex: q.orderIndex
                    };
                }

                if (q.saveToDatabase && savedQuestionsMap[q.questionText]) {
                    return {
                        questionId: savedQuestionsMap[q.questionText],
                        questionText: q.questionText,
                        orderIndex: q.orderIndex
                    };
                }

                return {
                    questionText: q.questionText,
                    orderIndex: q.orderIndex
                };
            });

            // Create the template
            const response = await axios.post('/api/v1/reports/templates', {
                schoolId,
                title: templateForm.title,
                questions: templateQuestions
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });

            updateTemplates([...templates, response.data]);
            setIsCreateModalOpen(false);
            resetTemplateForm();
            setError('');
            showNotification('Szablon został utworzony pomyślnie');
        } catch (err) {
            setError('Nie udało się utworzyć szablonu');
        } finally {
            setLoading(false);
        }
    };

    // Delete template
    const handleDeleteTemplate = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('jwt');
            await axios.delete(`/api/v1/reports/templates/${templateToDelete.id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            updateTemplates(templates.filter(t => t.id !== templateToDelete.id));
            setIsDeleteModalOpen(false);
            setTemplateToDelete(null);
            setError('');
            showNotification('Szablon został usunięty pomyślnie');
        } catch (err) {
            setError('Nie udało się usunąć szablonu');
        } finally {
            setLoading(false);
        }
    };

    // Handle delete from preview modal
    const handleDeleteFromPreview = (template) => {
        setIsPreviewModalOpen(false);
        setTemplateToPreview(null);
        setTemplateToDelete(template);
        setIsDeleteModalOpen(true);
    };

    // Open preview modal
    const openPreviewModal = (template) => {
        setTemplateToPreview(template);
        setIsPreviewModalOpen(true);
    };

    // Open assignment modal
    const openAssignModal = (template) => {
        setTemplateToAssign(template);
        setIsAssignModalOpen(true);
    };

    // Show notification
    const showNotification = (message, type = 'success') => {
        const notification = document.createElement('div');
        notification.className = type === 'success' ? 'tm-success-notification' : 'tm-error-notification';
        notification.textContent = message;
        document.body.appendChild(notification);
        setTimeout(() => {
            if (document.body.contains(notification)) {
                document.body.removeChild(notification);
            }
        }, 3000);
    };

    useEffect(() => {
        if (error) {
            showNotification(error, 'error');
            setError('');
        }
    }, [error]);

    return (
        <div className="tm-container">
            {/* Header with Create Button */}
            <div className="tm-header">
                <button
                    className="tm-create-btn"
                    onClick={() => setIsCreateModalOpen(true)}
                >
                    Utwórz szablon
                </button>
            </div>

            {/* Templates Grid */}
            {templates.length === 0 ? (
                <div className="tm-empty-state">
                    <FiFileText className="tm-empty-icon" />
                    <p>Brak szablonów</p>
                    <button
                        className="tm-create-btn"
                        onClick={() => setIsCreateModalOpen(true)}
                    >
                        Utwórz Pierwszy Szablon
                    </button>
                </div>
            ) : (
                <div className="tm-grid">
                    {templates.map((template) => (
                        <div key={template.id} className="tm-card">
                            <div className="tm-card-header">
                                <div className="tm-card-title-section">
                                    <h3 className="tm-card-title">{template.title}</h3>
                                    <p className="tm-card-count">
                                        Pytania ({template.questions?.length || 0})
                                    </p>
                                </div>
                                <button
                                    className="tm-card-delete-btn"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setTemplateToDelete(template);
                                        setIsDeleteModalOpen(true);
                                    }}
                                    title="Usuń szablon"
                                >
                                    <FiTrash2 />
                                </button>
                            </div>

                            <div className="tm-card-actions">
                                <button
                                    className="tm-btn-preview"
                                    onClick={() => openPreviewModal(template)}
                                >
                                    Podgląd
                                </button>
                                <button
                                    className="tm-btn-assign"
                                    onClick={() => openAssignModal(template)}
                                >
                                    Przypisz
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Create Template Modal */}
            {isCreateModalOpen && (
                <div className="tm-modal-overlay" onClick={() => { setIsCreateModalOpen(false); resetTemplateForm(); }}>
                    <div className="tm-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="tm-modal-header">
                            <h2 className="tm-modal-title">Utwórz Nowy Szablon</h2>
                        </div>

                        <div className="tm-modal-content">
                            <div className="tm-input-group">
                                <label htmlFor="templateTitle">Tytuł Szablonu</label>
                                <input
                                    id="templateTitle"
                                    type="text"
                                    value={templateForm.title}
                                    onChange={handleTitleChange}
                                    placeholder="Wprowadź tytuł szablonu..."
                                    className="tm-input"
                                />
                            </div>

                            <div className="tm-questions-section">
                                <h3 className="tm-section-title">Pytania w Szablonie</h3>

                                {/* Questions List */}
                                <div className="tm-questions-list">
                                    {templateForm.questions.length === 0 ? (
                                        <div className="tm-no-questions">
                                            Brak dodanych pytań. Dodaj pytania poniżej.
                                        </div>
                                    ) : (
                                        templateForm.questions.map((question, index) => (
                                            <div key={index} className="tm-question-item">
                                                <div className="tm-question-header">
                                                    <span className="tm-question-number">Pytanie {index + 1}</span>
                                                    <div className="tm-question-controls">
                                                        <button
                                                            type="button"
                                                            className="tm-move-btn"
                                                            onClick={() => moveQuestion(index, 'up')}
                                                            disabled={index === 0}
                                                        >
                                                            <FiChevronUp />
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className="tm-move-btn"
                                                            onClick={() => moveQuestion(index, 'down')}
                                                            disabled={index === templateForm.questions.length - 1}
                                                        >
                                                            <FiChevronDown />
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className="tm-remove-btn"
                                                            onClick={() => removeQuestion(index)}
                                                        >
                                                            <FiTrash2 />
                                                        </button>
                                                    </div>
                                                </div>
                                                <div className="tm-question-content">
                                                    <p className="tm-question-text">{question.questionText}</p>
                                                    {question.isExistingQuestion && (
                                                        <span className="tm-badge tm-badge-existing">
                                                            <FiHelpCircle /> Zapisane pytanie
                                                        </span>
                                                    )}
                                                    {!question.isExistingQuestion && question.saveToDatabase && (
                                                        <span className="tm-badge tm-badge-save">
                                                            <FiSave /> Zostanie zapisane
                                                        </span>
                                                    )}
                                                    {!question.isExistingQuestion && !question.saveToDatabase && (
                                                        <span className="tm-badge tm-badge-temp">
                                                            <FiFileText /> Tylko w szablonie
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        ))
                                    )}
                                </div>

                                {/* Add Question Section */}
                                <div className="tm-add-question-section">
                                    <h4>Dodaj nowe pytanie</h4>

                                    <div className="tm-mode-selector">
                                        <label className={`tm-mode-option ${questionMode === 'saved' ? 'tm-mode-active' : ''}`}>
                                            <input
                                                type="radio"
                                                name="questionMode"
                                                value="saved"
                                                checked={questionMode === 'saved'}
                                                onChange={() => setQuestionMode('saved')}
                                            />
                                            <div className="tm-mode-content">
                                                <FiHelpCircle className="tm-mode-icon" />
                                                <span>Użyj zapisanego pytania</span>
                                            </div>
                                        </label>

                                        <label className={`tm-mode-option ${questionMode === 'new-save' ? 'tm-mode-active' : ''}`}>
                                            <input
                                                type="radio"
                                                name="questionMode"
                                                value="new-save"
                                                checked={questionMode === 'new-save'}
                                                onChange={() => setQuestionMode('new-save')}
                                            />
                                            <div className="tm-mode-content">
                                                <FiSave className="tm-mode-icon" />
                                                <span>Dodaj nowe i zapisz</span>
                                            </div>
                                        </label>

                                        <label className={`tm-mode-option ${questionMode === 'new-temp' ? 'tm-mode-active' : ''}`}>
                                            <input
                                                type="radio"
                                                name="questionMode"
                                                value="new-temp"
                                                checked={questionMode === 'new-temp'}
                                                onChange={() => setQuestionMode('new-temp')}
                                            />
                                            <div className="tm-mode-content">
                                                <FiFileText className="tm-mode-icon" />
                                                <span>Dodaj tylko do szablonu</span>
                                            </div>
                                        </label>
                                    </div>

                                    <div className="tm-question-input-area">
                                        {questionMode === 'saved' ? (
                                            <div className="tm-input-group">
                                                <label>Wybierz zapisane pytanie</label>
                                                <select
                                                    className="tm-select"
                                                    value={selectedSavedQuestion}
                                                    onChange={(e) => setSelectedSavedQuestion(e.target.value)}
                                                >
                                                    <option value="">-- Wybierz pytanie --</option>
                                                    {savedQuestions.map((question) => (
                                                        <option key={question.id} value={question.id}>
                                                            {question.question}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                        ) : (
                                            <div className="tm-input-group">
                                                <label>
                                                    Nowe pytanie
                                                    {questionMode === 'new-save' && (
                                                        <span className="tm-input-badge tm-input-badge-save">Zostanie zapisane</span>
                                                    )}
                                                    {questionMode === 'new-temp' && (
                                                        <span className="tm-input-badge tm-input-badge-temp">Tylko w szablonie</span>
                                                    )}
                                                </label>
                                                <textarea
                                                    value={newQuestionText}
                                                    onChange={(e) => setNewQuestionText(e.target.value)}
                                                    placeholder="Wprowadź treść pytania..."
                                                    rows="3"
                                                    className="tm-textarea"
                                                />
                                            </div>
                                        )}

                                        <button
                                            type="button"
                                            className="tm-add-question-btn"
                                            onClick={addQuestionToTemplate}
                                            disabled={
                                                (questionMode === 'saved' && !selectedSavedQuestion) ||
                                                ((questionMode === 'new-save' || questionMode === 'new-temp') && !newQuestionText.trim())
                                            }
                                        >
                                            <FiPlus /> Dodaj do szablonu
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="tm-modal-footer">
                            <button
                                className="tm-btn-cancel"
                                onClick={() => {
                                    setIsCreateModalOpen(false);
                                    resetTemplateForm();
                                }}
                                disabled={loading}
                            >
                                Anuluj
                            </button>
                            <button
                                className="tm-btn-submit"
                                onClick={handleCreateTemplate}
                                disabled={loading || !templateForm.title.trim() || templateForm.questions.length === 0}
                            >
                                {loading ? 'Tworzenie...' : 'Utwórz Szablon'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Preview Modal */}
            <ReportTemplatePreviewModal
                isOpen={isPreviewModalOpen}
                onClose={() => {
                    setIsPreviewModalOpen(false);
                    setTemplateToPreview(null);
                }}
                template={templateToPreview}
                onDelete={handleDeleteFromPreview}
            />

            {/* Assignment Modal */}
            <ReportAssignmentModal
                isOpen={isAssignModalOpen}
                onClose={() => {
                    setIsAssignModalOpen(false);
                    setTemplateToAssign(null);
                }}
                template={templateToAssign}
                schoolId={schoolId}
            />

            {/* Delete Confirmation Modal */}
            <DeleteConfirmationModal
                isOpen={isDeleteModalOpen}
                onClose={() => {
                    setIsDeleteModalOpen(false);
                    setTemplateToDelete(null);
                }}
                onConfirm={handleDeleteTemplate}
                itemName={`szablon "${templateToDelete?.title}"`}
            />
        </div>
    );
};

export default TemplatesManagement;